import { type ReactNode, useId, useRef } from "react";
import { createPortal } from "react-dom";

import { cn } from "./lib/cn";
import { useDismiss, useFocusTrap } from "./lib/useOverlay";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Erişilebilir başlık. Görsel olarak gizlenebilir ama HER ZAMAN gerekli. */
  title: string;
  /** Başlığı görsel olarak da göster. Gezinme drawer'ında genelde gereksiz. */
  showTitle?: boolean;
  side?: "left" | "right";
  children: ReactNode;
  /** Sticky altbilgi (mockup: Filtreler drawer'ı "Temizle" + "Uygula"). */
  footer?: ReactNode;
}

/**
 * Kenardan açılan panel (CLOS-97 `[058]`).
 *
 * `Modal` ile aynı erişilebilirlik çekirdeğini paylaşıyor (`useFocusTrap` + `useDismiss`,
 * ADR-0022): odak içeride kalıyor, ESC ve dışarı tıklama kapatıyor, kapanınca önceki odağa
 * dönülüyor. Ayrı bir bileşen çünkü sunumu farklı — kenara yaslı, tam yükseklik, kaydırma
 * davranışı ayrı.
 *
 * ⚠ `role="dialog"` + `aria-modal`: ekran okuyucuya arkadaki içeriğin şu an erişilemez
 * olduğunu söylüyor. Odak kapanı görsel olanı, bu ise anlamsal olanı sağlıyor; ikisi
 * birlikte gerekli.
 */
export function Drawer({
  open,
  onClose,
  title,
  showTitle = false,
  side = "left",
  children,
  footer,
}: DrawerProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useFocusTrap(panelRef, open);
  /**
   * `outsideClick` KAPALI: örtüye tıklama aşağıda ayrıca dinleniyor. İkisi birden açık
   * olsaydı `onClose` iki kez çağrılırdı (`Modal` ile aynı gerekçe).
   */
  useDismiss(panelRef, open, onClose);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        data-testid="drawer-overlay"
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-inverse/50"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          "absolute inset-y-0 flex w-[360px] max-w-[90vw] flex-col bg-surface shadow-md",
          side === "left" ? "left-0" : "right-0",
        )}
      >
        <header className="flex items-center justify-between gap-2 border-b border-border-default px-6 py-4">
          <h2
            id={titleId}
            /** Başlık gizliyken bile DOM'da: erişilebilir ad ondan geliyor. */
            className={showTitle ? "text-lg font-bold text-fg-strong" : "sr-only"}
          >
            {title}
          </h2>
          <button
            type="button"
            aria-label="Kapat"
            onClick={onClose}
            className="ml-auto flex size-9 shrink-0 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-muted hover:text-fg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
        {footer ? (
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-border-default px-6 py-4">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
